export type OriginCoords = { lat: number; lng: number }

type CityEntry = { keys: string[]; coords: OriginCoords }

/** Statyczny słownik współrzędnych — dopasowanie po znormalizowanym fragmencie tekstu pochodzenia. */
const CITY_ENTRIES: CityEntry[] = [
  { keys: ['wrocław', 'wroclaw', 'wroc'], coords: { lat: 51.1079, lng: 17.0385 } },
  { keys: ['legnica'], coords: { lat: 51.207, lng: 16.1619 } },
  { keys: ['wałbrzych', 'walbrzych', 'walbrzeg'], coords: { lat: 50.7714, lng: 16.2843 } },
  { keys: ['jelenia góra', 'jelenia gora', 'jelenia'], coords: { lat: 50.904, lng: 15.719 } },
  { keys: ['lubin'], coords: { lat: 51.4014, lng: 16.2019 } },
  { keys: ['głogów', 'glogow'], coords: { lat: 51.6634, lng: 16.0845 } },
  { keys: ['świdnica', 'swidnica'], coords: { lat: 50.8438, lng: 16.4885 } },
  { keys: ['bolesławiec', 'boleslawiec'], coords: { lat: 51.264, lng: 15.565 } },
  { keys: ['kłodzko', 'klodzko'], coords: { lat: 50.4372, lng: 16.6614 } },
  { keys: ['dzierżoniów', 'dzierzoniow'], coords: { lat: 50.7284, lng: 16.6514 } },
  { keys: ['oleśnica', 'olesnica'], coords: { lat: 51.2134, lng: 17.3799 } },
  { keys: ['oława', 'olawa'], coords: { lat: 50.9466, lng: 17.292 } },
  { keys: ['kamienna góra', 'kamienna gora'], coords: { lat: 50.7836, lng: 16.0303 } },
  { keys: ['nowa ruda'], coords: { lat: 50.5806, lng: 16.5014 } },
  { keys: ['lubań', 'luban'], coords: { lat: 51.1203, lng: 15.2917 } },
  { keys: ['zgorzelec', 'görlitz', 'gorlitz'], coords: { lat: 51.1492, lng: 15.0089 } },
  { keys: ['bielawa'], coords: { lat: 50.691, lng: 16.623 } },
  { keys: ['polkowice'], coords: { lat: 51.5039, lng: 16.0716 } },
  { keys: ['trzebnica'], coords: { lat: 51.3106, lng: 17.0633 } },
  { keys: ['brzeg'], coords: { lat: 50.8608, lng: 17.4674 } },
  { keys: ['nysa'], coords: { lat: 50.473, lng: 17.334 } },
  { keys: ['warszawa', 'warsaw'], coords: { lat: 52.2297, lng: 21.0122 } },
  { keys: ['kraków', 'krakow', 'cracow'], coords: { lat: 50.0647, lng: 19.945 } },
  { keys: ['poznań', 'poznan'], coords: { lat: 52.4064, lng: 16.9252 } },
  { keys: ['katowice'], coords: { lat: 50.2649, lng: 19.0238 } },
  { keys: ['gdańsk', 'gdansk'], coords: { lat: 54.352, lng: 18.6466 } },
  { keys: ['łódź', 'lodz'], coords: { lat: 51.7592, lng: 19.456 } },
  { keys: ['szczecin'], coords: { lat: 53.4285, lng: 14.5528 } },
  { keys: ['wrocławski', 'wroclawski'], coords: { lat: 51.1079, lng: 17.0385 } },
  { keys: ['praga', 'praha', 'prague'], coords: { lat: 50.0755, lng: 14.4378 } },
  { keys: ['czechy', 'czechia', 'česko', 'cesko'], coords: { lat: 49.8175, lng: 15.473 } },
  { keys: ['berlin'], coords: { lat: 52.52, lng: 13.405 } },
  { keys: ['niemcy', 'deutschland', 'germany'], coords: { lat: 51.1657, lng: 10.4515 } },
  { keys: ['drezno', 'dresden'], coords: { lat: 51.0504, lng: 13.7373 } },
  { keys: ['bystrzyca kłodzka', 'bystrzyca klodzka', 'bystrzyca'], coords: { lat: 50.2967, lng: 16.6518 } },
  { keys: ['strzelin'], coords: { lat: 50.9625, lng: 17.065 } },
  { keys: ['sandomierz'], coords: { lat: 50.6826, lng: 21.7489 } },
  { keys: ['bydgoszcz'], coords: { lat: 53.1235, lng: 18.0084 } },
  { keys: ['wąbrzeźno', 'wabrzezno'], coords: { lat: 53.2798, lng: 18.9477 } },
  { keys: ['dolny śląsk', 'dolnoslaskie', 'dolnośląskie', 'dolnyslask'], coords: { lat: 50.9, lng: 16.5 } },
  { keys: ['polska', 'poland'], coords: { lat: 52.0, lng: 19.0 } },
  { keys: ['wiedeń', 'wieden', 'wien', 'vienna'], coords: { lat: 48.2082, lng: 16.3738 } },
]

function normalizeOriginText(raw: string): string {
  return raw.trim().toLocaleLowerCase('pl-PL')
}

function originLookupCandidates(raw: string): string[] {
  const normalized = normalizeOriginText(raw)
  if (!normalized) return []

  const segments = normalized
    .split(/[/,|·–—-]+/)
    .map((part) => part.trim())
    .filter(Boolean)

  const candidates = [normalized, ...segments]
  return [...new Set(candidates)]
}

/** Zwraca współrzędne miasta na podstawie wpisu pochodzenia użytkownika. */
export function resolveOriginCoords(origin: string | null | undefined): OriginCoords | null {
  const candidates = originLookupCandidates(origin ?? '')
  if (!candidates.length) return null

  for (const candidate of candidates) {
    for (const entry of CITY_ENTRIES) {
      if (entry.keys.some((key) => candidate.includes(key))) {
        return entry.coords
      }
    }
  }

  return null
}

export type OriginMapPoint = {
  origin: string
  memberCount: number
  lat: number
  lng: number
}

export function originClustersToMapPoints(
  clusters: Array<{ origin: string; memberCount: number }>,
): OriginMapPoint[] {
  const byKey = new Map<string, OriginMapPoint>()

  for (const cluster of clusters) {
    const coords = resolveOriginCoords(cluster.origin)
    if (!coords) continue

    const key = `${coords.lat.toFixed(4)}:${coords.lng.toFixed(4)}`
    const existing = byKey.get(key)
    if (existing) {
      existing.memberCount += cluster.memberCount
      if (!existing.origin.includes(cluster.origin)) {
        existing.origin = `${existing.origin}, ${cluster.origin}`
      }
      continue
    }

    byKey.set(key, {
      origin: cluster.origin,
      memberCount: cluster.memberCount,
      lat: coords.lat,
      lng: coords.lng,
    })
  }

  return [...byKey.values()]
}

/** Pierwsze imię do publicznego tickera (bez pełnego nazwiska). */
export function publicDisplayFirstName(fullName: string | null | undefined): string | null {
  const trimmed = fullName?.trim()
  if (!trimmed) return null
  return trimmed.split(/\s+/)[0] ?? null
}
